import React, { useMemo } from 'react';
import { GwaData } from '../../lib/types';
import { useRescueContext } from '../../lib/RescueContext';
import { useToast } from '../../components/Toast/ToastContext';
import Button from '../../components/Button/Button';
import { trackGwaLinkCopied, trackGwaCodeCopied, trackGwaBlurbCopied } from '../../analytics';
import gwaData from '../../data/gwa.json';
import styles from './GWA.module.scss';

const data = gwaData as GwaData;

function resolveBlurb(template: string, orgName: string): string {
  return template
    .replace(/\{\{orgName\}\}/g, orgName)
    .replace(/\{\{pdpUrl\}\}/g, data.pdpUrl)
    .replace(/\{\{promoCode\}\}/g, data.promoCode);
}

function CopyIcon() {
  return (
    <svg
      className={styles.copyIcon}
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

export default function GWA() {
  const { orgName } = useRescueContext();
  const { showToast } = useToast();

  const emailBlurb = useMemo(() => resolveBlurb(data.shareBlurbs.email, orgName), [orgName]);
  const printBlurb = useMemo(() => resolveBlurb(data.shareBlurbs.print, orgName), [orgName]);

  function handleCopyUrl() {
    navigator.clipboard.writeText(data.pdpUrl);
    trackGwaLinkCopied();
    showToast('Link copied to clipboard');
  }

  function handleCopyCode() {
    navigator.clipboard.writeText(data.promoCode);
    trackGwaCodeCopied();
    showToast('Promo code copied to clipboard');
  }

  function handleCopyEmail() {
    navigator.clipboard.writeText(emailBlurb);
    trackGwaBlurbCopied('email');
    showToast('Email blurb copied to clipboard');
  }

  function handleCopyPrint() {
    navigator.clipboard.writeText(printBlurb);
    trackGwaBlurbCopied('print');
    showToast('Print blurb copied to clipboard');
  }

  return (
    <div>
      <h1>Gift with Adoption</h1>

      {/* Summary stat cards — Coming Soon */}
      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.cardLabel}>Page Views</div>
          <div className={styles.cardSubtext}>Coming Soon</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardLabel}>Redemptions</div>
          <div className={styles.cardSubtext}>Coming Soon</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardLabel}>Total Redemptions</div>
          <div className={styles.cardSubtext}>Coming Soon</div>
        </div>
      </div>

      {/* Your rescue's unique offer */}
      <div className={styles.offerCard}>
        <h2 className={styles.offerTitle}>Your rescue&rsquo;s unique offer</h2>
        <p className={styles.offerDescription}>
          Adopters who visit your link and apply your code get a Fi Mini GPS Tracker for{' '}
          <strong className={styles.highlight}>6 months free</strong>{' '}
          <span className={styles.highlight}>(renews at $189/yr)</span>. Share with recent and previous adopters &mdash;
          not for public or social media use.
        </p>
        <div className={styles.offerFields}>
          <div className={styles.offerField}>
            <div className={styles.offerFieldLabel}>Link</div>
            <div className={styles.offerFieldRow}>
              <span className={styles.offerFieldValue}>{data.pdpUrl}</span>
              <Button variant="green" size="sm" onClick={handleCopyUrl}>
                <CopyIcon />
                Copy
              </Button>
            </div>
          </div>
          <div className={styles.offerField}>
            <div className={styles.offerFieldLabel}>Promo Code</div>
            <div className={styles.offerFieldRow}>
              <span className={`${styles.offerFieldValue} ${styles.offerFieldMono}`}>{data.promoCode}</span>
              <Button variant="green" size="sm" onClick={handleCopyCode}>
                <CopyIcon />
                Copy
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Shareable blurbs */}
      <div className={styles.blurbsCard}>
        <h2 className={styles.blurbsTitle}>Shareable blurbs</h2>
        <p className={styles.blurbsDescription}>
          Pre-written messages to share with recent and previous adopters. Click &ldquo;Copy&rdquo; to copy to
          clipboard.
        </p>

        <div className={styles.blurbSection}>
          <div className={styles.blurbHeader}>
            <div className={styles.blurbLabel}>Email</div>
            <Button variant="green" size="sm" onClick={handleCopyEmail}>
              <CopyIcon />
              Copy
            </Button>
          </div>
          <pre className={styles.blurbPreview}>{emailBlurb}</pre>
        </div>

        <div className={styles.blurbSection}>
          <div className={styles.blurbHeader}>
            <div className={styles.blurbLabel}>Print / Handout</div>
            <Button variant="green" size="sm" onClick={handleCopyPrint}>
              <CopyIcon />
              Copy
            </Button>
          </div>
          <pre className={styles.blurbPreview}>{printBlurb}</pre>
        </div>
      </div>
    </div>
  );
}
